/**
 * Simple Router for Child App
 * Handles navigation between main screens
 */

class Router {
    constructor() {
        this.routes = new Map();
        this.currentRoute = null;
        this.currentView = null;
        this.currentArgs = [];
    }

    /**
     * Register a route
     */
    register(path, handler) {
        this.routes.set(path, handler);
    }

    /**
     * Navigate to a route
     */
    async navigate(path, ...args) {
        console.log(`🧭 Navigating to: ${path}`);

        const handler = this.routes.get(path);

        if (!handler) {
            console.error(`❌ No route handler found for: ${path}`);
            return;
        }

        try {
            // Store current route and the args it was given, so the same view
            // can be rebuilt later (see reload)
            this.currentRoute = path;
            this.currentArgs = args;

            // Call the route handler
            this.currentView = await handler(...args);

            // Update URL without page reload (optional)
            // Preserve query parameters (e.g. ?handoff=<token> for PWA install)
            // so that Add to Home Screen captures them in the launch URL.
            if (window.history && window.history.pushState) {
                const search = window.location.search || '';
                window.history.pushState({ path }, '', `${search}#${path}`);
            }

            console.log(`✅ Navigated to: ${path}`);
        } catch (error) {
            console.error(`❌ Error navigating to ${path}:`, error);
            throw error;
        }
    }

    /**
     * Rebuild the current route with the same arguments.
     *
     * Used when something outside the view changes what it should render — a
     * language switch, for example. Deliberately does not touch history or the
     * URL: the user has not gone anywhere, the view is just being rebuilt.
     *
     * @returns {Promise<void>}
     */
    async reload() {
        if (!this.currentRoute) return;

        const handler = this.routes.get(this.currentRoute);
        if (!handler) return;

        console.log(`🔄 Reloading route: ${this.currentRoute}`);
        this.currentView = await handler(...this.currentArgs);
    }

    /**
     * Get current route
     */
    getCurrentRoute() {
        return this.currentRoute;
    }

    /**
     * Get current view instance
     */
    getCurrentView() {
        return this.currentView;
    }
}

// Export singleton instance
export const router = new Router();
